import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowForwardIos } from "@mui/icons-material";
import Button from "@mui/material/Button";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ScoreColorCircle } from "@src/shared/components/ScoreColorCircle/ScoreColorCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { BlockWithExtraContent } from "@src/shared/components/BlockWithExtraContent/BlockWithExtraContent";
import { useSearchCandidateResult } from "../api/useCandidate";
import { CandidateMiniCard } from "@src/shared/components/CandidateMiniCard/CandidateMiniCard";
import { useRequesthResult } from "@src/shared/api/hooks/useRequestResult/useRequestResult";
import { copyToClipboard } from "../lib/copyToClipboard";
import {
  monthsToYearsString,
  getWordForm,
  yearsForms,
} from "@src/shared/utils/monthToYears";
import { LastWorkPlace } from "./LastWorkPlace/LastWorkPlace";
import { HeaderCandidate } from "./Header/HeaderCandidate";
import {
  ICandidate,
  ScoreDetailSection,
  TRequirementScore,
  TTotalScorePerReq,
} from "@src/shared/types";
import quotesImg from "@src/shared/assets/quotes.svg";
import { ScoreDetailsOverlay } from "./ScoreDetailsOverlay/ScoreDetailsOverlay";

import styles from "./styles.module.scss";

const SCORE_DETAILS_SECTIONS_CONFIG = [
  {
    key: "mandatory",
    title: "Обязательные требования",
  },
  {
    key: "priority",
    title: "Приоритетные требования",
  },
  {
    key: "optional",
    title: "Опциональные требования",
  },
] as const;

const getSectionMaxScore = (requirements: TRequirementScore[]) => {
  return requirements.reduce((sum, requirement) => {
    return sum + (requirement.weight || 0);
  }, 0);
};

const mapTotalScorePerReqToSections = (
  totalScorePerReq?: TTotalScorePerReq | null,
): ScoreDetailSection[] => {
  if (!totalScorePerReq) {
    return [];
  }

  return SCORE_DETAILS_SECTIONS_CONFIG.map(({ key, title }) => {
    const section = totalScorePerReq[key];

    if (!section?.requirements?.length) {
      return null;
    }

    return {
      id: key,
      title,
      score: section.total_score,
      maxScore: getSectionMaxScore(section.requirements),
      items: section.requirements.map((requirement) => ({
        id: requirement.skill_name,
        title: requirement.skill_name,
        score: requirement.score,
        maxScore: requirement.weight,
      })),
    };
  }).filter(Boolean) as ScoreDetailSection[];
};

const getCandidateMatchingScore = (candidate?: ICandidate | null) => {
  return (
    candidate?.total_score_per_req?.total_score ??
    candidate?.matching_score ??
    0
  );
};

export const Candidate = () => {
  const [searchParams] = useSearchParams();
  const [isScoreDetailsOpen, setIsScoreDetailsOpen] = useState(false);
  const candidate_id = searchParams.get("candidate_id");
  const request_uuid = searchParams.get("request_uuid");
  const { isLoading, getSearchCandidateResult, searchCandidateResult } =
    useSearchCandidateResult();
  const {
    isLoading: isLoadingRequestResult,
    getSearchRequestResult,
    searchRequestResult,
  } = useRequesthResult();

  const totalExperienceString = monthsToYearsString(
    searchCandidateResult?.total_experience || 0,
  );

  const relevantExperienceToString = monthsToYearsString(
    searchCandidateResult?.relevant_experience_duration,
  );

  const operatingPeriod = searchCandidateResult?.last_work_place?.period
    ? monthsToYearsString(searchCandidateResult?.last_work_place?.period)
    : null;

  const relevantExperienceInTotalExperience = Math.floor(
    (searchCandidateResult?.relevant_experience_duration /
      searchCandidateResult?.total_experience) *
      100,
  );

  const totalYearsExperience = Math.floor(
    (searchCandidateResult?.relevant_experience_duration || 0) / 12,
  );

  const totalYearsExperienceLabel = getWordForm(
    totalYearsExperience,
    yearsForms,
  );

  const totalMonthsExperience = totalYearsExperience
    ? null
    : monthsToYearsString(
        searchCandidateResult?.relevant_experience_duration || 0,
      ).split(" ");

  const candidateAgeLabel = getWordForm(searchCandidateResult?.age, yearsForms);

  const scoreDetailsSections = mapTotalScorePerReqToSections(
    searchCandidateResult?.total_score_per_req,
  );

  const scoreDetailsTotalScore =
    searchCandidateResult?.total_score_per_req?.total_score ??
    searchCandidateResult?.matching_score ??
    null;

  const renderCitizenship = () =>
    searchCandidateResult?.citizenship.length ? (
      searchCandidateResult?.citizenship?.map((citizen) => (
        <div key={citizen}>{citizen}</div>
      ))
    ) : (
      <div>-</div>
    );

  const renderPositiveSkills = () =>
    searchCandidateResult?.positive_skills.length ? (
      <>
        {searchCandidateResult?.positive_skills?.map(
          (positive_skill, index) => (
            <div className={styles.pmItem} key={index}>
              <div className={styles.pmIcon}>
                <CheckCircleIcon fontSize="small" sx={{ color: "#00C195" }} />
              </div>
              <div className={styles.pmText}>{positive_skill}</div>
            </div>
          ),
        )}
      </>
    ) : (
      <div>Отсутствуют</div>
    );

  const renderNegativeSkills = () =>
    searchCandidateResult?.negative_skills.length ? (
      <>
        {searchCandidateResult?.negative_skills?.map((negative_skill) => (
          <div className={styles.pmItem}>
            <div className={styles.pmIcon}>
              <CancelIcon fontSize="small" sx={{ color: "#FF0002" }} />
            </div>
            <div className={styles.pmText}>{negative_skill}</div>
          </div>
        ))}
      </>
    ) : (
      <div>Отсутствуют</div>
    );

  const renderOtherCandidate = () =>
    searchRequestResult.length ? (
      searchRequestResult
        ?.filter((candidat) => candidat.id !== candidate_id)
        ?.map((candidate) => {
          const relevantExperience = monthsToYearsString(
            candidate?.relevant_experience_duration,
          );

          const isShowNeedAtentionInfo = !!candidate?.warnings.length;
          const candidateScore = getCandidateMatchingScore(candidate);

          return (
            <div className={styles.otherCandidatesRowItem} key={candidate.id}>
              <CandidateMiniCard
                age={candidate.age}
                candidate_id={candidate.id}
                request_uuid={request_uuid || ""}
                desired_position={candidate.desired_position}
                city={candidate.city}
                relevant_experience_years={relevantExperience}
                salary={candidate.salary}
                scores={candidateScore}
                gender={candidate.gender}
                isShowNeedAtentionInfo={isShowNeedAtentionInfo}
                isViewed={candidate.viewed}
              />
            </div>
          );
        })
    ) : (
      <div>Отсутствуют данные по другим кандидатам</div>
    );

  const renderStudy = () =>
    searchCandidateResult.education.length ? (
      searchCandidateResult.education?.map((education) => (
        <div
          className={styles.educationItem}
          key={education.name_of_university}
        >
          <div className={styles.infoItem}>
            <div>Учебное заведение:</div>
            <div>{education.name_of_university || "-"}</div>
          </div>
          <div className={styles.infoItem}>
            <div>Специальность:</div>
            <div>{education.specialization || "-"}</div>
          </div>
          <div className={styles.infoItem}>
            <div>Год окончания:</div>
            <div>{education.year || "-"}</div>
          </div>
        </div>
      ))
    ) : (
      <div>Отсутствуют данные по образованию</div>
    );

  const renderKeySkills = () => {
    return (
      <>
        {searchCandidateResult?.skills_influenced.map((keySkill) => (
          <div key={keySkill} className={styles.keySkill}>
            {keySkill}
          </div>
        ))}
      </>
    );
  };

  useEffect(() => {
    if (candidate_id && request_uuid) {
      getSearchCandidateResult(request_uuid, candidate_id);
      getSearchRequestResult(request_uuid);
    }
  }, [request_uuid, candidate_id]);

  if (!candidate_id && !request_uuid) {
    return (
      <div className={styles.candidateWrap}>
        Отсутствует request_uuid и id кандидата
      </div>
    );
  }

  if (isLoading) {
    return <div className={styles.candidateWrap}>Загрузка...</div>;
  }

  return (
    <div className={styles.candidateWrap}>
      <div className={styles.breadcrumbs}>
        <div>Результаты подбора</div>
        <div>
          <ArrowForwardIos sx={{ width: "10px", height: "10px" }} />
        </div>
        <div>{searchCandidateResult?.desired_position || "-"}</div>
        <div>
          <ArrowForwardIos sx={{ width: "10px", height: "10px" }} />
        </div>
        <div>Кандидат</div>
      </div>
      <div className={styles.content}>
        <HeaderCandidate
          searchCandidateResult={searchCandidateResult}
          searchRequestResult={searchRequestResult}
          isOverlay={isScoreDetailsOpen}
        />
        <div className={styles.experienceRow}>
          <div className={styles.scoreWrap}>
            <div className={styles.scoreAndExperience}>
              <div className={styles.score}>
                <div className={styles.scoreTitle}>Балл соответствия</div>
                <div className={styles.scoreBtnWrapper}>
                  <div
                    className={styles.scoreBtn}
                    onClick={() => setIsScoreDetailsOpen(true)}
                  >
                    <img src={quotesImg} />
                    <span>Подробнее</span>
                  </div>
                </div>
                <div className={styles.scoreCricle}>
                  <ScoreColorCircle
                    score={scoreDetailsTotalScore}
                    size={180}
                    strokeWidth={10}
                    fontSize={44}
                    labelSize={24}
                  />
                </div>
                <div className={styles.scoreFooterExperience}>
                  <div className={styles.scoreFooterTextExp}>
                    <div className={styles.scoreFooterText}>Соответствие</div>
                    <div className={styles.scoreScore}>
                      {scoreDetailsTotalScore} из 100 баллов
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.experience}>
                <div className={styles.scoreTitle}>Релевантный опыт</div>
                <div className={styles.scoreCricle}>
                  <ScoreColorCircle
                    score={relevantExperienceInTotalExperience || 0}
                    size={180}
                    strokeWidth={10}
                    titleInsteadScore={totalYearsExperience}
                    labelText={totalYearsExperienceLabel}
                    totalMonthsExperience={totalMonthsExperience}
                    fontSize={44}
                    labelSize={24}
                    isShowExperience={true}
                  />
                </div>
                <div className={styles.scoreFooterExp}>
                  <div className={styles.scoreFooterTextExp}>
                    <div className={styles.scoreFooterText}>Опыт</div>
                    <div className={styles.scoreScore}>
                      {totalExperienceString}
                    </div>
                  </div>
                  <div className={styles.scoreFooterTextExpRelev}>
                    <div className={styles.scoreFooterText}>Релевантный</div>
                    <div className={styles.scoreScore}>
                      {relevantExperienceToString}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.skillsWrap}>
            <div className={styles.skillsPlusMinus}>
              <div className={styles.keySkills}>
                <BlockWithExtraContent
                  blockName="Ключевые навыки"
                  heightInPx={374}
                  widthInPx={396}
                  itemsContent={renderKeySkills}
                />
              </div>
              <div className={styles.plus}>
                <BlockWithExtraContent
                  blockName="Плюсы"
                  heightInPx={374}
                  widthInPx={396}
                  itemsContent={renderPositiveSkills}
                />
              </div>
              <div className={styles.minus}>
                <BlockWithExtraContent
                  blockName="Минусы"
                  heightInPx={374}
                  widthInPx={396}
                  itemsContent={renderNegativeSkills}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoLeftColumn}>
            <div className={styles.commonInfo}>
              <div className={styles.commonBlockTitle}>
                <div className={styles.commonBlockTitleImg}>
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ color: "#00C195" }}
                  />
                </div>
                <div>Общая информация</div>
              </div>

              <div className={styles.infoItem}>
                <div>Возраст:</div>
                <div>
                  {searchCandidateResult?.age || "-"} {candidateAgeLabel}
                </div>
              </div>

              <div className={styles.infoItem}>
                <div>Гражданство:</div>
                <>{renderCitizenship()}</>
              </div>

              <div className={styles.infoItem}>
                <div>Город проживания:</div>
                <div>{searchCandidateResult?.city || "-"}</div>
              </div>

              <div className={styles.infoItem}>
                <div>Готовность к переезду:</div>
                <div>{searchCandidateResult?.relocation || "-"}</div>
              </div>

              <div className={styles.infoItem}>
                <div>Готовность к командировкам:</div>
                <div>
                  {searchCandidateResult?.business_trip_readiness || "-"}
                </div>
              </div>
            </div>

            <div className={styles.education}>
              <div className={styles.commonBlockTitle}>
                <div className={styles.commonBlockTitleImg}>
                  <SchoolOutlinedIcon
                    fontSize="small"
                    sx={{ color: "#00C195" }}
                  />
                </div>
                <div>Образование</div>
              </div>

              <div>{renderStudy()}</div>
            </div>
          </div>

          <LastWorkPlace
            companyName={searchCandidateResult?.last_work_place?.company}
            position={searchCandidateResult?.last_work_place?.position}
            period={operatingPeriod}
            schedules={searchCandidateResult?.last_work_place?.work_formats?.join(
              ", ",
            )}
          />

          <div className={styles.reasons}>
            <div className={styles.commonBlockTitle}>
              <div className={styles.commonBlockTitleImg}>
                <SearchIcon fontSize="small" sx={{ color: "#00C195" }} />
              </div>
              <div>Причины</div>
            </div>

            <div>{searchCandidateResult?.role_fit_analysis}</div>

            <div className={styles.reasonsCopyButtonWrap}>
              <div className={styles.reasonsCopyButton}>
                <Button
                  startIcon={<ContentCopyIcon />}
                  variant="text"
                  size="small"
                  sx={{ color: "#A4BBC7" }}
                  onClick={() =>
                    copyToClipboard(searchCandidateResult?.role_fit_analysis || "")
                  }
                >
                  Копировать
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.otherCandidatesWrap}>
          <div className={styles.otherCandidates}>
            <div className={styles.otherCandidatesTitle}>Другие кандидаты</div>
            <div className={styles.otherCandidateRow}>
              {isLoadingRequestResult ? (
                <>Загрузка...</>
              ) : (
                renderOtherCandidate()
              )}
            </div>
          </div>
        </div>
      </div>
      <ScoreDetailsOverlay
        isOpen={isScoreDetailsOpen}
        onClose={() => setIsScoreDetailsOpen(false)}
        totalScore={scoreDetailsTotalScore}
        sections={scoreDetailsSections}
        searchCandidateResult={searchCandidateResult}
        searchRequestResult={searchRequestResult}
      />
    </div>
  );
};
